/**
 * Per-room cache in IndexedDB: world manifest + edit-log state, so any peer
 * that played in a room can re-host it ("Resume" in the Multiplayer screen).
 * The block delta itself lives in the room's WorldSave namespace
 * (localStorage key opencraft.mp.<code>), which every peer keeps current.
 */

import { openNetDb } from './identity';
import type { Bounds } from './editlog';

export interface RoomCache {
  code: string;
  worldId: string;
  seed: number;
  wgv: number;
  lastSeq: number;
  lastTerm: number;
  bounds: Bounds;
  savedAt: number;
  name?: string;
}

const STORE = 'worlds';

export async function saveRoomCache(c: RoomCache): Promise<void> {
  const db = await openNetDb();
  if (!db) return;
  await new Promise<void>((res) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(c, c.code);
    tx.oncomplete = () => res();
    tx.onerror = () => res();
  });
}

export async function listRoomCaches(): Promise<RoomCache[]> {
  const db = await openNetDb();
  if (!db) return [];
  return new Promise((res) => {
    const r = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
    r.onsuccess = () => res((r.result as RoomCache[]).sort((a, b) => b.savedAt - a.savedAt));
    r.onerror = () => res([]);
  });
}

export async function loadRoomCache(code: string): Promise<RoomCache | null> {
  const db = await openNetDb();
  if (!db) return null;
  return new Promise((res) => {
    const r = db.transaction(STORE, 'readonly').objectStore(STORE).get(code);
    r.onsuccess = () => res((r.result as RoomCache | undefined) ?? null);
    r.onerror = () => res(null);
  });
}
